import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { UtensilsCrossed, Minus, Plus } from 'lucide-react';

const quickAmounts = [5, 10, 25, 50];

interface KgCounterProps {
  value: number;
  onChange: (value: number) => void;
}

export const KgCounter: React.FC<KgCounterProps> = ({ value, onChange }) => {
  const { t } = useTranslation();

  const increment = (amount: number) => onChange(value + amount);
  const decrement = (amount: number) => onChange(Math.max(value - amount, 0));
  const reset = () => onChange(0);

  return (
    <Card className="p-6 text-center">
      <div className="flex items-center justify-center gap-2 mb-4">
        <UtensilsCrossed className="w-8 h-8 text-warning" />
        <span className="text-lg font-medium">{t('cbs.feed.howMuchKg')}</span>
      </div>

      {/* Big Number Display with Unit */}
      <div className="flex items-end justify-center gap-2 mb-6">
        <span className="text-7xl font-bold text-foreground">{value}</span>
        <span className="text-2xl text-muted-foreground mb-2">
          {t('common.units.kg')}
        </span>
      </div>

      {/* Large +/- Buttons */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <Button
          variant="outline"
          className="h-20 text-3xl"
          onClick={() => decrement(1)}
          disabled={value === 0}
        >
          <Minus className="w-10 h-10" />
        </Button>
        <Button
          variant="outline"
          className="h-20 text-3xl"
          onClick={() => increment(1)}
        >
          <Plus className="w-10 h-10" />
        </Button>
      </div>

      {/* Quick Add Buttons - Visual Bags */}
      <div className="grid grid-cols-4 gap-2">
        {quickAmounts.map((kg) => (
          <Button
            key={kg}
            variant="secondary"
            className="h-16 flex-col gap-1"
            onClick={() => increment(kg)}
          >
            <span className="text-xl">🛍️</span>
            <span className="font-bold">+{kg}</span>
          </Button>
        ))}
      </div>

      {/* Reset Button */}
      <Button
        variant="ghost"
        className="mt-4 text-muted-foreground"
        onClick={reset}
      >
        🔄 {t('common.reset')}
      </Button>
    </Card>
  );
};

export default KgCounter;
